/**
 * Incoming-call ringtone for desktop + mobile.
 *
 * Multi-tab: when one tab Accepts/Declines, ALL tabs must stop ringing.
 * Uses BroadcastChannel + localStorage so every open tab stops audio.
 *
 * Architecture:
 * 1. Web Audio API (AudioContext + AudioBufferSourceNode):
 *    Decodes ringtone.mp3 into memory. Plays with 0ms buffering latency without browser blocks.
 * 2. HTMLAudioElement (<audio>):
 *    Pre-buffered secondary channel with in-flight Promise protection against AbortError race conditions.
 * 3. Platform Detection:
 *    Yellow user-gesture button / blocked message is restricted exclusively to iPhone/iOS.
 *    On PC / Windows / Mac desktop, ringtone initializes and plays automatically without manual intervention.
 */
import ringtoneSrc from "../assets/music/ringtone.mp3";

let audio = null;
let unlocked = false;
let unlockListenersBound = false;
let crossTabBound = false;
let ringing = false;
let playBlocked = false;
let retryTimer = null;
let vibrateTimer = null;
let audioCtx = null;
let ringtoneAudioBuffer = null;
let isBufferLoading = false;
let webAudioSource = null;
let isWebAudioPlaying = false;
let isPlayPending = false;
let oscTimer = null;
let activeAppointmentId = null;
const blockedListeners = new Set();
const stopListeners = new Set();

const BC_NAME = "prontopsy_ringtone";
const STORAGE_STOP_KEY = "prontopsy_ringtone_stop";
const STORAGE_RING_KEY = "prontopsy_ringtone_active";

export function isIOS() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || window.opera || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function notifyBlocked() {
  // Only notify blocked = true on iPhone/iOS; PC / desktop plays automatically without button prompts
  const isBlocked = isIOS() ? playBlocked : false;
  blockedListeners.forEach((fn) => {
    try {
      fn(isBlocked);
    } catch (_) {
      /* ignore */
    }
  });
}

function notifyStopped(payload) {
  stopListeners.forEach((fn) => {
    try {
      fn(payload);
    } catch (_) {
      /* ignore */
    }
  });
}

export function subscribeRingtoneBlocked(fn) {
  if (typeof fn !== "function") return () => {};
  blockedListeners.add(fn);
  try {
    const isBlocked = isIOS() ? playBlocked : false;
    fn(isBlocked);
  } catch (_) {
    /* ignore */
  }
  return () => blockedListeners.delete(fn);
}

/** Called when ANY tab stops the ring (accept/decline/end) */
export function subscribeRingtoneStopped(fn) {
  if (typeof fn !== "function") return () => {};
  stopListeners.add(fn);
  return () => stopListeners.delete(fn);
}

export function isRingtonePlayBlocked() {
  // Only iPhone/iOS requires manual user interaction prompt; PC / Windows / Mac desktop never blocks UI
  if (!isIOS()) return false;
  return playBlocked;
}

export function isRingtoneUnlocked() {
  return unlocked;
}

export function isRingtonePlaying() {
  return ringing;
}

function getAudioContext() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) {
    try {
      audioCtx = new Ctx();
    } catch (_) {
      return null;
    }
  }
  return audioCtx;
}

async function loadRingtoneBuffer() {
  if (ringtoneAudioBuffer) return ringtoneAudioBuffer;
  if (isBufferLoading || typeof window === "undefined") return null;
  const ctx = getAudioContext();
  if (!ctx) return null;
  isBufferLoading = true;
  try {
    const resp = await fetch(ringtoneSrc);
    const arrayBuffer = await resp.arrayBuffer();
    ringtoneAudioBuffer = await ctx.decodeAudioData(arrayBuffer);
    return ringtoneAudioBuffer;
  } catch (err) {
    return null;
  } finally {
    isBufferLoading = false;
  }
}

function startWebAudioRingtone() {
  if (isWebAudioPlaying && webAudioSource) return true;
  const ctx = getAudioContext();
  if (!ctx || !ringtoneAudioBuffer) return false;
  try {
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    if (ctx.state !== "running") return false;
    stopWebAudioRingtone();
    const srcNode = ctx.createBufferSource();
    srcNode.buffer = ringtoneAudioBuffer;
    srcNode.loop = true;
    const gainNode = ctx.createGain();
    gainNode.gain.value = 1.0;
    srcNode.connect(gainNode);
    gainNode.connect(ctx.destination);
    srcNode.start(0);
    webAudioSource = srcNode;
    isWebAudioPlaying = true;
    return true;
  } catch (err) {
    isWebAudioPlaying = false;
    return false;
  }
}

function stopWebAudioRingtone() {
  if (webAudioSource) {
    try {
      webAudioSource.stop();
      webAudioSource.disconnect();
    } catch (_) {
      /* ignore */
    }
    webAudioSource = null;
  }
  isWebAudioPlaying = false;
}

function ensureAudio() {
  if (typeof Audio === "undefined") return null;
  if (!audio) {
    audio = new Audio();
    audio.loop = true;
    audio.preload = "auto";
    audio.playsInline = true;
    audio.setAttribute("playsinline", "true");
    audio.setAttribute("webkit-playsinline", "true");
  }
  return audio;
}

function ensureRingtoneSrc() {
  const a = ensureAudio();
  if (!a) return null;
  if (!a.src || a.src === window.location.href || a.src.endsWith("/")) {
    a.src = ringtoneSrc;
    try {
      a.load();
    } catch (_) {
      /* ignore */
    }
  }
  return a;
}

function broadcastStop(reason, appointmentId) {
  if (typeof window === "undefined") return;
  const payload = {
    type: "stop",
    reason: reason || "accepted",
    appointmentId: appointmentId || activeAppointmentId || null,
    ts: Date.now(),
  };
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const bc = new BroadcastChannel(BC_NAME);
      bc.postMessage(payload);
      bc.close();
    }
  } catch (_) {
    /* ignore */
  }
  try {
    // storage event fires in OTHER tabs only
    localStorage.setItem(STORAGE_STOP_KEY, JSON.stringify(payload));
    localStorage.removeItem(STORAGE_RING_KEY);
  } catch (_) {
    /* ignore */
  }
}

/** Wire once: stop this tab when another tab accepts/declines */
export function bindRingtoneCrossTab() {
  if (crossTabBound || typeof window === "undefined") return;
  crossTabBound = true;

  const onStopMessage = (payload) => {
    if (!payload || payload.type !== "stop") return;
    // Local stop without re-broadcast loop
    stopRingtoneLocal();
    notifyStopped(payload);
  };

  try {
    if (typeof BroadcastChannel !== "undefined") {
      const bc = new BroadcastChannel(BC_NAME);
      bc.onmessage = (ev) => onStopMessage(ev?.data);
    }
  } catch (_) {
    /* ignore */
  }

  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_STOP_KEY && e.newValue) {
      try {
        onStopMessage(JSON.parse(e.newValue));
      } catch (_) {
        onStopMessage({ type: "stop" });
      }
    }
  });
}

/** Silent 1-sample wav — unlock AudioContext without starting ringtone media session */
const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAAA";

/**
 * Unlock autoplay policy with SILENT audio and AudioContext resume.
 * Preloads ringtone buffer into memory so first incoming call rings immediately.
 */
export async function unlockRingtoneFromGesture() {
  const ctx = getAudioContext();

  if (ctx && ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch (_) {
      /* ignore */
    }
  }

  // Preload buffer into memory
  loadRingtoneBuffer();

  // Ensure HTMLAudio element has source ready in browser cache
  const a = ensureRingtoneSrc();
  if (a) {
    try {
      a.load();
    } catch (_) {
      /* ignore */
    }
  }

  try {
    const silent = new Audio(SILENT_WAV);
    silent.volume = 0.01;
    silent.setAttribute("playsinline", "true");
    const p = silent.play();
    if (p && typeof p.then === "function") {
      await p;
    }
    silent.pause();
    silent.removeAttribute("src");
    silent.load?.();
  } catch (_) {
    /* ignore */
  }

  unlocked = true;
  playBlocked = false;
  notifyBlocked();
  return unlocked;
}

export function bindRingtoneUnlock() {
  if (unlockListenersBound || typeof document === "undefined") return;
  unlockListenersBound = true;
  ensureRingtoneSrc();
  bindRingtoneCrossTab();
  // Preload buffer in background on load
  loadRingtoneBuffer();

  const onGesture = () => {
    unlockRingtoneFromGesture();
  };

  document.addEventListener("touchstart", onGesture, { capture: true, passive: true });
  document.addEventListener("touchend", onGesture, { capture: true, passive: true });
  document.addEventListener("click", onGesture, { capture: true, passive: true });
  document.addEventListener("pointerdown", onGesture, { capture: true, passive: true });
  document.addEventListener("mousedown", onGesture, { capture: true, passive: true });
  document.addEventListener("keydown", onGesture, { capture: true, passive: true });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && ringing) {
      attemptPlay();
    }
  });
}

function stopFallbackBeep() {
  if (oscTimer) {
    clearInterval(oscTimer);
    oscTimer = null;
  }
}

function startFallbackBeep() {
  stopFallbackBeep();
  const ctx = getAudioContext();
  if (!ctx) return;

  const beep = () => {
    if (!ringing) return;
    try {
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      setTimeout(() => {
        try {
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
          osc.stop(ctx.currentTime + 0.16);
        } catch (_) {
          try {
            osc.stop();
          } catch (__) {
            /* ignore */
          }
        }
      }, 180);
    } catch (_) {
      /* ignore */
    }
  };

  beep();
  oscTimer = setInterval(beep, 1400);
}

function startVibrateLoop() {
  stopVibrateLoop();
  if (!navigator.vibrate) return;
  const pulse = () => {
    if (!ringing) return;
    try {
      navigator.vibrate([400, 200, 400, 200, 400]);
    } catch (_) {
      /* ignore */
    }
  };
  pulse();
  vibrateTimer = setInterval(pulse, 2000);
}

function stopVibrateLoop() {
  if (vibrateTimer) {
    clearInterval(vibrateTimer);
    vibrateTimer = null;
  }
  try {
    if (navigator.vibrate) navigator.vibrate(0);
  } catch (_) {
    /* ignore */
  }
}

function attemptPlay() {
  if (!ringing) return;

  // 1. Play via Web Audio (instant buffer, unlocked across all browsers once resumed)
  const webAudioOk = startWebAudioRingtone();
  if (webAudioOk) {
    playBlocked = false;
    notifyBlocked();
    stopFallbackBeep();
    return;
  }

  // If buffer is still loading, start Web Audio as soon as it arrives
  if (!ringtoneAudioBuffer) {
    loadRingtoneBuffer().then((buf) => {
      if (buf && ringing) {
        if (startWebAudioRingtone()) {
          playBlocked = false;
          notifyBlocked();
          stopFallbackBeep();
        }
      }
    });
  }

  // 2. Play via HTMLAudioElement secondary channel with race-condition protection
  const a = ensureRingtoneSrc();
  if (!a) return;

  if (!a.paused && !a.ended && a.currentTime > 0) {
    playBlocked = false;
    stopFallbackBeep();
    return;
  }

  if (isPlayPending) {
    // In-flight play promise buffering; do not interrupt with a new play()
    return;
  }

  try {
    a.loop = true;
    a.muted = false;
    a.volume = 1;
    isPlayPending = true;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        isPlayPending = false;
        playBlocked = false;
        notifyBlocked();
        stopFallbackBeep();
      }).catch((err) => {
        isPlayPending = false;
        // AbortError is an interrupted request (e.g. from load/state), NOT an autoplay block
        if (err?.name === "AbortError") {
          return;
        }
        // Only mark blocked if Web Audio is also NOT playing
        if (!isWebAudioPlaying) {
          playBlocked = true;
          notifyBlocked();
          if (isIOS()) startFallbackBeep();
        }
      });
    } else {
      isPlayPending = false;
      playBlocked = false;
      notifyBlocked();
      stopFallbackBeep();
    }
  } catch (err) {
    isPlayPending = false;
    if (err?.name !== "AbortError" && !isWebAudioPlaying) {
      playBlocked = true;
      notifyBlocked();
      if (isIOS()) startFallbackBeep();
    }
  }
}

/** Stop audio in THIS tab only (no rebroadcast) */
function stopRingtoneLocal() {
  ringing = false;
  playBlocked = false;
  activeAppointmentId = null;
  isPlayPending = false;
  notifyBlocked();
  if (retryTimer) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
  stopFallbackBeep();
  stopVibrateLoop();
  stopWebAudioRingtone();
  try {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  } catch (_) {
    /* ignore */
  }
  try {
    if (typeof navigator !== "undefined" && navigator.mediaSession) {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = "none";
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * Stop ringtone in THIS tab and tell all other tabs to stop too.
 * @param {string} [reason]
 * @param {string} [appointmentId]
 */
export function stopRingtone(reason, appointmentId) {
  const appt = appointmentId || activeAppointmentId;
  stopRingtoneLocal();
  try {
    localStorage.removeItem(STORAGE_RING_KEY);
  } catch (_) {
    /* ignore */
  }
  // Only broadcast + dismiss for real hang-up / accept / decline.
  // Effect cleanup ("modal-close") must NOT mark dismissed — that killed the next ring.
  const DISMISS = new Set([
    "accepted",
    "decline",
    "reject",
    "disconnect",
    "chat-accepted",
  ]);
  const shouldDismiss = DISMISS.has(String(reason || ""));
  if (shouldDismiss) {
    broadcastStop(reason || "stop", appt);
    try {
      if (appt) {
        sessionStorage.setItem(`ringDismissed:${appt}`, "1");
      }
    } catch (_) {
      /* ignore */
    }
  }
}

/**
 * Start looping ringtone. Safe to call multiple times.
 * @param {string} [appointmentId] — used so other tabs can dismiss this ring
 */
export function startRingtone(appointmentId) {
  bindRingtoneCrossTab();

  // Don't ring if this appointment was already accepted/declined in another tab
  if (appointmentId) {
    try {
      if (sessionStorage.getItem(`ringDismissed:${appointmentId}`) === "1") {
        return;
      }
    } catch (_) {
      /* ignore */
    }
    // Same live ring — do not restart (second play() + fallback beep = duplicate)
    if (ringing && activeAppointmentId === String(appointmentId)) {
      if (isWebAudioPlaying || (audio && !audio.paused && !playBlocked)) return;
    }
    activeAppointmentId = String(appointmentId);
    try {
      localStorage.setItem(
        STORAGE_RING_KEY,
        JSON.stringify({ appointmentId: activeAppointmentId, ts: Date.now() }),
      );
    } catch (_) {
      /* ignore */
    }
  } else if (ringing && (isWebAudioPlaying || (audio && !audio.paused && !playBlocked))) {
    return;
  }

  ringing = true;
  ensureRingtoneSrc();
  startVibrateLoop();
  attemptPlay();

  if (retryTimer) clearInterval(retryTimer);
  retryTimer = setInterval(() => {
    if (!ringing) {
      clearInterval(retryTimer);
      retryTimer = null;
      return;
    }
    attemptPlay();
  }, 500);
}

export async function startRingtoneFromUserGesture(appointmentId) {
  await unlockRingtoneFromGesture();
  startRingtone(appointmentId);
  attemptPlay();
  return !playBlocked;
}

export function getRingtoneAudio() {
  return ensureAudio();
}
